// One employee's tax year, month by month: the engine behind the salary table.
//
// Pure: the employee, their attendance summary and the tax year go in, rows come
// out. No clock, no locale, no React. The payroll Lambda
// (backend-amplify: function/payrollRun/src/salary_year.py) is a port of this
// file, and both are held to the same cases in
// src/Utils/__tests__/fixtures/pph21-cases.json. Change one, change the other.
import {
  calculateGrossFromNet,
  calculateGrossSalary,
  calculateLastTaxPeriod,
  calculateTERRate,
  getTERCategory,
  POSITION_COST_MAX,
  POSITION_COST_PERCENTAGE,
} from "../Constants/terRates";
import { calculateAttendanceDeduction } from "./attendanceDeduction";
import { monthKey, toCalendarDate } from "./calendarDate";
import {
  filterBenefitsByMonth,
  getContributionWage,
  getDeductionAmount,
  getEmployerPremiums,
  isEmployerContributionAllowance,
} from "./employeeBenefits";
import { getAnnualisation, getTaxYearEndDate } from "./taxSubject";

const sumAmounts = (items) =>
  items.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);

// One month of the tax year. `yearlyValues` carries the running totals the last
// tax period is settled against, and is updated in place.
const calculateMonthlyValues = ({
  employee,
  isGrossUp,
  netSalary,
  totalTaxableAllowances,
  terCategory,
  yearlyValues,
  isLastTaxPeriod,
  monthlyDeductionAmount,
  annualise,
  monthsWorked,
}) => {
  let grossSalary, monthlyTax;
  // Only the last tax period knows these: they are the figures the year's tax
  // was actually assessed on, annualized where PMK 168/2023 Lampiran B angka
  // I.2.1.2 requires it.
  let yearlyTaxableIncome = null;
  let yearlyTax = null;

  // In the last tax period, gross-up is still recorded on a net basis: that
  // month's tax allowance is the unknown, so it cannot add to cumulative gross
  // yet.
  if (isGrossUp && !isLastTaxPeriod) {
    grossSalary = calculateGrossFromNet(
      netSalary,
      totalTaxableAllowances,
      terCategory,
    );
  } else {
    grossSalary = calculateGrossSalary(netSalary, totalTaxableAllowances);
  }

  yearlyValues.grossSalary += grossSalary;
  yearlyValues.deductions += monthlyDeductionAmount;
  yearlyValues.positionCost += Math.min(
    grossSalary * POSITION_COST_PERCENTAGE,
    POSITION_COST_MAX / 12,
  );

  if (isLastTaxPeriod) {
    ({ monthlyTax, yearlyTaxableIncome, yearlyTax } = calculateLastTaxPeriod({
      yearlyGrossSalary: yearlyValues.grossSalary,
      yearlyPositionCost: yearlyValues.positionCost,
      yearlyDeductions: yearlyValues.deductions,
      yearlyTaxPaid: yearlyValues.taxPaid,
      marriageStatus: employee.marriageStatus,
      dependent: employee.dependent,
      isGrossUp,
      annualise,
      monthsWorked,
      // Under gross-up the allowance solved for lands in this month's bruto,
      // and carries its own position cost with it.
      lastPeriodGross: grossSalary,
    }));

    if (isGrossUp) {
      // The tax allowance is only known now, so fold it into gross.
      grossSalary = netSalary + monthlyTax + totalTaxableAllowances;
      yearlyValues.grossSalary += monthlyTax;
    }
  } else {
    monthlyTax = grossSalary * calculateTERRate(grossSalary, terCategory);
    // What is actually withheld is whole rupiah, the figure the row shows.
    // Settling the last period against the unrounded sum left the rows a few
    // rupiah short of the year's tax.
    yearlyValues.taxPaid += Math.floor(monthlyTax);
  }

  return {
    grossSalary,
    monthlyTax,
    yearlyTaxableIncome:
      yearlyTaxableIncome === null ? null : Math.floor(yearlyTaxableIncome),
    yearlyTax: yearlyTax === null ? null : Math.floor(yearlyTax),
  };
};

// The months of `year` the employee is paid in, first and last as 1-12. Null
// when none are: the employee starts after the year, or their tax year ended
// before it began.
const getPaidMonths = (employee, year) => {
  const start = toCalendarDate(employee?.startDate);
  if (!start) return null;

  const startYear = Number(start.slice(0, 4));
  if (startYear > year) return null;

  // The last tax period: the end of employment or the end of the subjective
  // tax liability, whichever comes first.
  const taxYearEnd = getTaxYearEndDate(employee);
  if (taxYearEnd && taxYearEnd.year() < year) return null;

  const firstMonth = startYear === year ? Number(start.slice(5, 7)) : 1;
  const lastMonth =
    taxYearEnd && taxYearEnd.year() === year ? taxYearEnd.month() + 1 : 12;

  return firstMonth > lastMonth ? null : { firstMonth, lastMonth };
};

export const buildSalaryYear = ({ employee, attendanceSummary, year }) => {
  if (!employee?.startDate || !employee?.baseSalary) return [];

  const paidMonths = getPaidMonths(employee, year);
  if (!paidMonths) return [];

  const baseSalary = Number(employee.baseSalary) || 0;
  const isGrossUp = employee.taxMethod === "GROSS_UP";
  const terCategory = getTERCategory(
    employee.marriageStatus,
    employee.dependent,
  );
  const { annualise, monthsWorked } = getAnnualisation(employee, year);

  const allowanceItems = employee?.allowances?.items || [];
  const deductionItems = employee?.deductions?.items || [];

  const yearlyValues = {
    grossSalary: 0,
    positionCost: 0,
    deductions: 0,
    taxPaid: 0,
  };

  const rows = [];

  for (
    let month = paidMonths.firstMonth;
    month <= paidMonths.lastMonth;
    month += 1
  ) {
    // Only what is in force this month counts: a monthly item between its own
    // start and end, an annual one in its anniversary month every year, a
    // one-off in the month it falls in. An allowance that ended in June is not
    // paid in July, and THR comes round again the next year.
    const period = { year, month };
    const allowances = filterBenefitsByMonth(allowanceItems, period);
    const deductions = filterBenefitsByMonth(deductionItems, period);
    const inForce = {
      ...employee,
      allowances: { items: allowances },
      deductions: { items: deductions },
    };

    // BPJS is priced on this month's upah, at the rates in force this month: a
    // ceiling that moves in March leaves January and February owed the old one.
    // Local midnight of the 1st, so the schedule reads the intended day in any
    // zone.
    const monthDate = new Date(year, month - 1, 1);
    const contributionWage = getContributionWage(inForce);
    const priceDeductions = (items) =>
      items.reduce(
        (sum, item) =>
          sum + getDeductionAmount(item, contributionWage, monthDate),
        0,
      );

    // BPJS Kesehatan 4%, JKK and JKM paid by the employer land in bruto. A
    // stored JKK/JKM allowance is left out of the taxable allowances: the
    // premium is derived here, and counting the snapshot too would put it in
    // bruto twice.
    const employerPremiums = getEmployerPremiums(inForce);
    const totalTaxableAllowances =
      sumAmounts(
        allowances.filter(
          (item) => item.isTaxable && !isEmployerContributionAllowance(item),
        ),
      ) + employerPremiums.total;

    const totalTaxableDeductions = priceDeductions(
      deductions.filter((item) => item.reduceIncome),
    );

    // A month with no attendance records deducts nothing, and PP 36/2021
    // Pasal 65 caps the month's deductions as a whole, so a loan repayment that
    // does not reduce taxable income still eats into the ceiling.
    const key = monthKey(year, month);
    const attendance = calculateAttendanceDeduction({
      summary: attendanceSummary?.[key],
      baseSalary,
      otherDeductions: priceDeductions(deductions),
    });

    const netSalary = baseSalary - attendance.amount;
    const isLastTaxPeriod = month === paidMonths.lastMonth || month === 12;

    const { grossSalary, monthlyTax, yearlyTaxableIncome, yearlyTax } =
      calculateMonthlyValues({
        employee,
        isGrossUp,
        netSalary,
        totalTaxableAllowances,
        terCategory,
        yearlyValues,
        isLastTaxPeriod,
        monthlyDeductionAmount: totalTaxableDeductions,
        annualise,
        monthsWorked,
      });

    rows.push({
      key,
      year,
      month,
      baseSalary: Math.floor(baseSalary),
      taxableAllowances: Math.floor(totalTaxableAllowances),
      employerPremiums: {
        health: Math.floor(employerPremiums.health),
        jkk: Math.floor(employerPremiums.jkk),
        jkm: Math.floor(employerPremiums.jkm),
      },
      taxAllowance: isGrossUp ? Math.floor(monthlyTax) : 0,
      attendanceDeduction: Math.floor(attendance.amount),
      attendanceDeductionUncapped: Math.floor(attendance.uncappedAmount),
      isAttendanceDeductionCapped: attendance.isCapped,
      hasAttendanceData: attendance.hasData,
      monthlyDeductionAmount: Math.floor(totalTaxableDeductions),
      grossSalary: Math.floor(grossSalary),
      terCategory,
      terRate: calculateTERRate(grossSalary, terCategory),
      isLastTaxPeriod,
      isGrossUp,
      taxAmount: Math.floor(monthlyTax),
      taxMethod: employee.taxMethod,
      yearlyTaxableIncome,
      yearlyTax,
    });
  }

  return rows;
};
